import { Primer } from '@primer-io/checkout-web';
import {
  appendPrimerTextToSubmitButton,
  formatPaymentProviders,
  getStyleConfig,
  handleClickEvent,
  handleKeyupEvent,
  updateClientSession,
  validateNameField,
} from '../config/primer';
import { fetcher } from '../fetcher';
import { formLoader } from '../payments';
import { buildPaymentInitializationPayload, getPaymentInitialization } from './payment-initialization';
import { cardholderIdActions } from '../common/validator/cardholder-id-handle';

let primerInstance = null;
const provider = 'primer';
const defaultOptions = {
  flow: 'web',
  onPaymentSuccess: null,
  onFormLoaded: null,
};

/**
 * Initialize Primer payment form with the user token(injected payment info).
 *
 * @param payload
 * @param additionalOptions
 */
export function initPaymentFormPrimer(payload, additionalOptions = defaultOptions) {
  getPaymentInitialization(payload, 'payment-session')
    .then(async response => {
      primerInstance = await Primer.showUniversalCheckout(response.token, {
        // Specify the selector of the container element
        container: '#primer-form-container',
        style: getStyleConfig(),
        paypal: {
          paymentFlow: 'PREFER_VAULT',
        },
        async onBeforePaymentCreate(data, handler) {
          await fetcher.post(new Url().build('payments', 'payment-intent'), payload);

          if (!cardholderIdActions.dom.cardholderIdInput) {
            payments.paymentFormCloseButton.hide();
            formLoader.show();
            return handler.continuePaymentCreation();
          }

          const nameInputField = document.querySelector('#primer-checkout-card-cardholder-name-input');
          const isNameValid = validateNameField(nameInputField);
          const isCardholderIdValid = cardholderIdActions.validateCardholderIdField();
          if (isCardholderIdValid && isNameValid) {
            payments.paymentFormCloseButton.hide();
            formLoader.show();
            await updateClientSession(response.token);
            await primerInstance.refreshClientSession();
            return handler.continuePaymentCreation();
          }

          return handler.abortPaymentCreation();
        },
        // Notifies you when a specific payment method has been selected or unselected.
        onPaymentMethodAction(action) {
          if (action === 'PAYMENT_METHOD_UNSELECTED') {
            payments.paymentFormCloseButton.show();
            formLoader.hide();
          }
        },
        /**
         * When the checkout flow has been completed, you'll receive
         * the successful payment via `onCheckoutComplete`.
         * Implement this callback to redirect the user to an order confirmation page and fulfill the order.
         */
        onCheckoutComplete({ payment }) {
          let isPaid = backEndVars.isSubscription === 'subscription' ? 'subscription/is-paid' : 'is-paid';
          payload.pageSlug = location.href.substring(location.href.lastIndexOf('/') + 1).replace(/(\?|#)(.*)/, ''); // get the page slug from the URL and strip the hash params and query string part (if any)

          formLoader.changeMessage();

          payments.flows[additionalOptions.flow](
            provider,
            new Url().build('products', payload.purchasable_id, isPaid),
            payload,
            true,
          );
        },

        /**
         * When the checkout flow has been fail, you'll receive
         * the error via `onCheckoutFail`
         */
        onCheckoutFail(error, { payment }, handler) {
          formLoader.hide();
          payments.paymentFormCloseButton.show();
          if (!handler) {
            return;
          }

          return handler.showErrorMessage();
        },

        /**
         * Learn more about the other options at:
         * https://primer.io/docs
         * https://www.npmjs.com/package/@primer-io/checkout-web
         */
      });

      await primerInstance.refreshClientSession();
      formLoader.hide();
      appendPrimerTextToSubmitButton();
      additionalOptions.onFormLoaded && additionalOptions.onFormLoaded();
      formatPaymentProviders();
      cardholderIdActions.init();

      inputEventHandler();
    })
    .catch(({ error }) => {
      payments.paymentFormCloseButton.show();
    });
}

/**
 * Input event handler method for the primer form. Needed for the error messages and styles.
 */
export function inputEventHandler() {
  const nameInputField = document.querySelector('#primer-checkout-card-cardholder-name-input');
  const submitButton = document.querySelector('#primer-checkout-submit-button');

  handleKeyupEvent(nameInputField, cardholderIdActions.dom.cardholderIdInput);
  handleClickEvent(submitButton);
}

/**
 * Teardown the primer form. Needed for cleaning the cache.
 */
export function teardownPrimer() {
  if (primerInstance && window.location.hash === '#primer-card-payment') {
    primerInstance.teardown();
  }
}

export const handlePaymentProcessPrimer = data => {
  fetcher
    .post(backEndVars.primerUrl, {
      uuid: data.uuid,
      customerUuid: data.customerUuid,
      email: store.getAnswers()['saved-email'] || null,
      paymentMethod: provider,
      price: parseFloat(data.amount),
      plan: data.planId,
      bundleId: data.bundleId,
      pageSlug: backEndVars.pageSlug || '',
      checkoutOffer: backEndVars.checkoutOffer || '',
      type: backEndVars.type || '',
    })
    .then(response => {
      payments.checkPaymentStatusAndRedirect(provider, new Url().build('products', response.uuid, 'is-paid'), response);
    })
    .catch(() => {
      // TODO - Close payment window!
      payments.formError.display(provider, 'Something went wrong. Please try again');
      payments.payButton.enable();
      payments.applePayButtons.enable();
    });
};

/**
 * Start Primer sequence payment(subscription or one-time), with existing payment method token.
 *
 * @param data
 */
export function startPaymentProcessPrimer(data) {
  getPaymentInitialization(
    buildPaymentInitializationPayload({
      provider: provider,
      uuid: data.uuid,
    }),
  ).then(async response => {
    handlePaymentProcessPrimer(data);
  });
}
